import { Fragment, useContext, useId, useMemo, useState, type MutableRefObject } from 'react'
import { ChevronUp, ChevronDown, Trash2, GripVertical, ImageOff, Package, Lock, Globe, PencilLine, StickyNote } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { WireArticleBlock, WireArticleFull, WireInlineBlock, WirePhotoItem, WireArticleNote } from '../../api/types'
import type { WireArtifact } from '../../api/types/articles'
import { SourceListContext } from './editor/SourceListContext'
import { ParagraphBlockEditor } from './editor/ParagraphBlockEditor'
import { HeadingBlockEditor } from './editor/HeadingBlockEditor'
import { ListBlockEditor } from './editor/ListBlockEditor'
import { ValidationAnnotations } from './ValidationAnnotations'
import { PortionMark } from '../article-viewer/common/PortionMark'
import type { ValidationError } from './validator/types'
import { getTableVariantStyles, TABLE_BG_CLASSES } from '../tableVariants'

// ─── Block chrome header (page-editor style) ─────────────────────────────────
// Shared by both photo blocks (outer) and photo-group items (inner).

interface BlockChromeHeaderProps {
  label: string
  idx: number
  total: number
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  /** When true, renders slightly more compact for inner photo items */
  compact?: boolean
  /** dnd-kit listeners + attributes — makes this header the drag handle */
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
  /** When true, hides drag handle and edit controls */
  readOnly?: boolean
}

function BlockChromeHeader({ label, idx, total, onMoveUp, onMoveDown, onRemove, compact, dragHandleProps, readOnly }: BlockChromeHeaderProps) {
  const py = compact ? 'py-1' : 'py-1.5'
  const textSize = compact ? 'text-[11px]' : 'text-xs'
  return (
    <div
      className={`flex items-center gap-2 border-b border-line bg-surface px-2 ${py} ${readOnly ? 'cursor-default' : 'touch-none cursor-grab active:cursor-grabbing'}`}
      {...(readOnly ? {} : dragHandleProps)}
    >
      {!readOnly && <GripVertical size={compact ? 12 : 14} className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true" />}
      <span className={`w-4 text-center font-bold text-fg-secondary ${textSize}`}>{idx + 1}</span>
      <span className={`flex-1 font-semibold text-fg ${textSize}`}>{label}</span>
      {!readOnly && (
        <>
          <button
            type="button"
            title="Move up"
            disabled={idx === 0}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveUp() }}
            className={`rounded px-1.5 py-0.5 text-fg-secondary hover:bg-fill hover:text-fg disabled:opacity-30 ${textSize}`}
          >↑</button>
          <button
            type="button"
            title="Move down"
            disabled={idx === total - 1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveDown() }}
            className={`rounded px-1.5 py-0.5 text-fg-secondary hover:bg-fill hover:text-fg disabled:opacity-30 ${textSize}`}
          >↓</button>
          <button
            type="button"
            title="Remove"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className={`rounded px-1.5 py-0.5 text-fg-secondary hover:bg-error-fill hover:text-error-fg ${textSize}`}
          >✕</button>
        </>
      )}
    </div>
  )
}

// ─── Compact sortable photo row (used inside photo-group block body) ─────────

function SortablePhotoRow({ photo, index, imageUrl }: { photo: WirePhotoItem; index: number; imageUrl: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id })
  const [imgError, setImgError] = useState(false)
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex cursor-grab touch-none items-center gap-2.5 px-3 py-2 active:cursor-grabbing ${
        isDragging ? 'opacity-40' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <span aria-hidden="true" className="shrink-0 text-fg-secondary opacity-30">
        <GripVertical size={11} />
      </span>
      <span className="w-4 shrink-0 text-center text-[10px] font-bold text-fg-secondary">
        {index + 1}
      </span>
      {imgError ? (
        <div className="flex h-9 w-14 shrink-0 items-center justify-center rounded bg-fill" aria-hidden="true">
          <ImageOff size={10} className="text-fg-secondary" />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="h-9 w-14 shrink-0 rounded object-cover"
          onError={() => setImgError(true)}
        />
      )}
      <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
        {photo.caption ?? photo.altText ?? (
          <span className="italic text-fg-secondary">Photo {index + 1}</span>
        )}
      </p>
      {photo.classification && (
        <PortionMark classification={photo.classification} disseminationControls={photo.disseminationControls} />
      )}
    </li>
  )
}

// ─── Sortable comparison slot (used inside photo-comparison block body) ────────

type PhotoComparisonBlock = Extract<WireArticleBlock, { type: 'photo-comparison' }>

function SortableComparisonSlot({ slot, slotLabel, imageUrl }: { slot: WirePhotoItem; slotLabel: string; imageUrl: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slot.id })
  const [imgError, setImgError] = useState(false)
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex cursor-grab touch-none items-center gap-2.5 px-3 py-2 active:cursor-grabbing ${
        isDragging ? 'opacity-40' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <span aria-hidden="true" className="shrink-0 text-fg-secondary opacity-30">
        <GripVertical size={11} />
      </span>
      <span className="w-12 shrink-0 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
        {slotLabel}
      </span>
      {imgError ? (
        <div className="flex h-9 w-14 shrink-0 items-center justify-center rounded bg-fill" aria-hidden="true">
          <ImageOff size={10} className="text-fg-secondary" />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="h-9 w-14 shrink-0 rounded object-cover"
          onError={() => setImgError(true)}
        />
      )}
      <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
        {slot.caption ?? slot.altText ?? (
          <span className="italic text-fg-secondary">No image</span>
        )}
      </p>
      {slot.classification && (
        <PortionMark classification={slot.classification} disseminationControls={slot.disseminationControls} />
      )}
    </li>
  )
}

// ─── Photo comparison draggable slot list ────────────────────────────────────

function PhotoComparisonSlotList({
  block,
  onSwap,
  artifacts,
}: {
  block: PhotoComparisonBlock
  onSwap: (blockId: string) => void
  artifacts: WireArtifact[]
}) {
  const dndId = useId()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )
  const slots = [
    { slot: block.before, label: block.beforeLabel ?? 'Before' },
    { slot: block.after,  label: block.afterLabel  ?? 'After' },
  ]
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    onSwap(block.id)
  }
  return (
    <DndContext id={dndId} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={slots.map(s => s.slot.id)} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-line">
          {slots.map(({ slot, label }) => (
            <SortableComparisonSlot
              key={slot.id}
              slot={slot}
              slotLabel={label}
              imageUrl={artifacts.find(a => a.id === slot.artifactId)?.presignedUrl ?? ''}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

// ─── Photo group draggable list ───────────────────────────────────────────────

type PhotoGroupBlock = Extract<WireArticleBlock, { type: 'photo-group' }>

function PhotoGroupPhotoList({
  block,
  onReorder,
  artifacts,
}: {
  block: PhotoGroupBlock
  onReorder: (blockId: string, fromIdx: number, toIdx: number) => void
  artifacts: WireArtifact[]
}) {
  const dndId = useId()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = block.photos.findIndex(p => p.id === active.id)
    const newIndex = block.photos.findIndex(p => p.id === over.id)
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorder(block.id, oldIndex, newIndex)
    }
  }
  return (
    <DndContext id={dndId} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={block.photos.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-line">
          {block.photos.map((photo, idx) => (
            <SortablePhotoRow
              key={photo.id}
              photo={photo}
              index={idx}
              imageUrl={artifacts.find(a => a.id === photo.artifactId)?.presignedUrl ?? ''}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

// ─── Inline content ───────────────────────────────────────────────────────────────────────────────────

function InlineContent({ children }: { children: WireInlineBlock[] }) {
  const sources = useContext(SourceListContext)
  return (
    <>
      {children.map((node, i) => {
        if (node.type === 'span') {
          const cls = [
            node.typography?.includes('text-bold') ? 'font-bold' : '',
            node.typography?.includes('text-italic') ? 'italic' : '',
            node.typography?.includes('text-smallcaps') ? 'font-variant-small-caps' : '',
            node.typography?.includes('text-superscript') ? 'align-super text-[0.75em]' : '',
          ].filter(Boolean).join(' ')
          return <span key={i} className={cls || undefined}>{node.text}</span>
        }
        if (node.type === 'link') {
          return (
            <a key={i} href={node.url} className="underline text-fg" target="_blank" rel="noreferrer">
              {node.text}
            </a>
          )
        }
        if (node.type === 'citation') {
          const idx = sources.findIndex(s => s.id === node.sourceId)
          return (
            <sup key={i} className="text-[0.75em] font-semibold text-fg-secondary">
              [{idx >= 0 ? idx + 1 : '?'}]
            </sup>
          )
        }
        return null
      })}
    </>
  )
}


// ─── Section type labels (mirror ToC shelf) ───────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  'summary':            'Key Judgments',
  'scope-note':         'Scope Note',
  'methodology-note':   'Methodology Note',
  'tradecraft-summary': 'Tradecraft Summary',
  'for-additional-info':'For Additional Info',
  'appendix':           'Appendix',
}

// ─── Block renderer ───────────────────────────────────────────────────────────

function BlockRenderer({ block }: { block: WireArticleBlock }) {
  switch (block.type) {

    case 'classification-banner':
      return (
        <div className="rounded bg-amber-100 px-4 py-1.5 text-center text-xs font-bold uppercase tracking-widest text-amber-900 dark:bg-amber-900/30 dark:text-amber-300">
          ({[block.classification, ...(block.disseminationControls ?? [])].join('//')})
        </div>
      )

    case 'heading':
      return (
        <div>
          {block.level <= 2
            ? <h2 className="font-zodiak text-xl font-bold text-fg">{block.content}</h2>
            : <h3 className="text-base font-bold text-fg">{block.content}</h3>
          }
        </div>
      )

    case 'paragraph':
      return (
        <p className="text-sm leading-relaxed text-fg">
          <PortionMark classification={block.classification} disseminationControls={block.disseminationControls} />
          <InlineContent children={block.children} />
        </p>
      )

    case 'section-marker':
      return (
        <div className="mb-1 mt-4 border-t-2 border-accent-primary-line pt-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-accent-primary-fg">
            {SECTION_LABELS[block.sectionType] ?? block.sectionType.replace(/-/g, ' ')}
          </p>
        </div>
      )

    case 'report-details':
    case 'attribution':
      // These duplicate article-level metadata shown in the metadata drawer.
      // Suppressed from canvas view until block partition model is decided.
      return null

    case 'warning':
      return (
        <div className="rounded border border-amber-400 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          {block.content}
        </div>
      )

    case 'list':
      return (
        <div>
          <PortionMark classification={block.classification} disseminationControls={block.disseminationControls} />
          {block.style === 'unordered' ? (
            <ul className="list-disc space-y-1 pl-5">
              {block.items.map(item => (
                <li key={item.id} className="text-sm leading-relaxed text-fg">
                  <InlineContent children={item.children} />
                </li>
              ))}
            </ul>
          ) : (
            <ol className="list-decimal space-y-1 pl-5">
              {block.items.map(item => (
                <li key={item.id} className="text-sm leading-relaxed text-fg">
                  <InlineContent children={item.children} />
                </li>
              ))}
            </ol>
          )}
        </div>
      )

    case 'textbox':
      return (
        <div className="space-y-3 rounded border border-line bg-fill px-5 py-4">
          {block.title && (
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-fg-secondary">
              {block.title}
            </p>
          )}
          {block.children.map(child => (
            <BlockRenderer key={child.id} block={child} />
          ))}
        </div>
      )

    case 'source-list':
      return (
        <div className="border-t border-line pt-4">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-fg-secondary">Sources</p>
          <ol className="list-decimal space-y-1 pl-5">
            {block.entries.map(e => (
              <li key={e.id} className="text-xs text-fg-secondary">{e.text}</li>
            ))}
          </ol>
        </div>
      )

    case 'table': {
      const headerRows = block.rows.filter(r => r.rowType === 'header')
      const bodyRows   = block.rows.filter(r => r.rowType === 'body')
      const v = getTableVariantStyles(block.variant)
      const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' } as const
      return (
        <div className="overflow-x-auto">
          {block.caption && (
            <p className="px-3 py-2 text-xs text-fg-secondary">{block.caption}</p>
          )}
          <table className={`text-xs ${v.table}`}>
            {headerRows.length > 0 && (
              <thead>
                {headerRows.map((row, ri) => (
                  <tr key={ri} className={v.headerTr}>
                    {row.cells.map((cell, ci) => (
                      <th key={ci} colSpan={cell.colSpan}
                        className={`${v.headerTh(ci)} ${cell.align || block.columnStyles[ci]?.align ? ALIGN_CLASS[cell.align ?? block.columnStyles[ci]?.align!] : ''}`}
                      >
                        <InlineContent>{cell.children}</InlineContent>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, ri) => {
                const hasRowBg = !!(row.highlighted || row.bg)
                const rowBg = row.highlighted
                  ? 'bg-yellow-3'
                  : row.bg
                    ? TABLE_BG_CLASSES[row.bg]
                    : v.bodyRowBg(ri)

                return (
                  <tr key={ri} className={`${v.bodyTrBase(ri)} ${rowBg}${row.bold ? ' font-bold' : ''}`}>
                    {row.cells.map((cell, ci) => {
                      const colBg = !hasRowBg && block.columnStyles[ci]?.bg
                        ? TABLE_BG_CLASSES[block.columnStyles[ci].bg!]
                        : ''
                      const cellBg = colBg || (!hasRowBg ? v.bodyCellBg(ci) : '')
                      const colAlign = cell.align ?? block.columnStyles[ci]?.align
                      const isRowHeader = ci === 0 && block.hasHeaderColumn

                      return isRowHeader ? (
                        <th key={ci} colSpan={cell.colSpan}
                          className={`${v.bodyRowHeaderThBase()} ${hasRowBg ? rowBg : v.bodyRowHeaderBg()} ${colAlign ? ALIGN_CLASS[colAlign] : ''}`}
                        >
                          <InlineContent>{cell.children}</InlineContent>
                        </th>
                      ) : (
                        <td key={ci} colSpan={cell.colSpan}
                          className={`${v.bodyTdBase()} ${cellBg} ${colAlign ? ALIGN_CLASS[colAlign] : ''}`}
                        >
                          <InlineContent>{cell.children}</InlineContent>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }

    default:
      return null
  }
}

// ─── Block note gutter ────────────────────────────────────────────────────────

interface BlockNoteGutterProps {
  blockId: string
  notes: WireArticleNote[]
  onAddNote: (blockId: string, content: string, isPrivate: boolean) => void
  onDeleteNote: (noteId: string) => void
  notesReadOnly?: boolean
}

function BlockNoteGutter({ blockId, notes, onAddNote, onDeleteNote, notesReadOnly }: BlockNoteGutterProps) {
  const [composing, setComposing] = useState(false)
  const [content, setContent] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)

  function handleSubmit() {
    const trimmed = content.trim()
    if (!trimmed) return
    onAddNote(blockId, trimmed, isPrivate)
    setContent('')
    setComposing(false)
  }

  return (
    <div className="space-y-1.5">
      {!notesReadOnly && !composing ? (
        <button
          type="button"
          onClick={() => setComposing(true)}
          aria-label="Add note"
          title="Add note"
          className="inline-flex h-6 w-6 items-center justify-center rounded border border-dashed border-line text-fg-secondary transition-colors hover:border-violet-7 hover:text-violet-11"
        >
          <PencilLine size={10} aria-hidden="true" />
        </button>
      ) : !notesReadOnly ? (
        <div className={`rounded border px-2 py-1.5 ${
          isPrivate ? 'border-amber-6 bg-amber-1' : 'border-violet-6 bg-violet-1'
        }`}>
          <div className="mb-1.5 flex gap-1">
            <button
              type="button"
              onClick={() => setIsPrivate(false)}
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${
                !isPrivate ? 'bg-violet-9 text-white' : 'text-fg-secondary hover:bg-fill'
              }`}
            >
              Team
            </button>
            <button
              type="button"
              onClick={() => setIsPrivate(true)}
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${
                isPrivate ? 'bg-amber-9 text-white' : 'text-fg-secondary hover:bg-fill'
              }`}
            >
              Private
            </button>
          </div>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit() }}
            placeholder="Add a note… (⌘↵ to save)"
            rows={2}
            autoFocus
            className={`w-full resize-none rounded border bg-canvas p-1 text-[11px] text-fg placeholder-fg-secondary outline-none ${
              isPrivate ? 'border-amber-6 focus:border-amber-8' : 'border-violet-6 focus:border-violet-8'
            }`}
          />
          <div className="mt-1 flex justify-end gap-1">
            <button
              type="button"
              onClick={() => { setComposing(false); setContent('') }}
              className="rounded px-2 py-0.5 text-[10px] text-fg-secondary hover:bg-fill"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!content.trim()}
              className="rounded bg-violet-9 px-2 py-0.5 text-[10px] font-semibold text-white transition-colors hover:bg-violet-10 disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>
      ) : null}
      {notes.map(note => (
        <div
          key={note.noteId}
          className={`rounded-lg border border-dashed px-2 py-1.5 text-[11px] shadow-sm ${
            note.visibilityPrivate
              ? 'border-amber-5 bg-amber-1'
              : 'border-violet-5 bg-violet-1'
          }`}
        >
          <div className="flex items-start justify-between gap-1">
            <div className={`flex min-w-0 items-center gap-1 font-semibold ${note.visibilityPrivate ? 'text-amber-11' : 'text-violet-11'}`}>
              <StickyNote size={9} aria-hidden="true" />
              {note.visibilityPrivate
                ? <Lock size={9} aria-hidden="true" />
                : <Globe size={9} aria-hidden="true" />}
              <span className="truncate">{shortDn(note.submitter)}</span>
            </div>
            {!notesReadOnly && (
              <button
                type="button"
                title="Resolve note"
                onClick={() => onDeleteNote(note.noteId)}
                className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold text-fg-secondary underline-offset-2 transition-colors hover:text-error-fg hover:underline"
              >
                Resolve
              </button>
            )}
          </div>
          <p className="mt-0.5 leading-snug text-fg">{note.content}</p>
        </div>
      ))}
    </div>
  )
}

function shortDn(dn: string): string {
  const cn = dn.match(/^CN=([^,]+)/i)?.[1]
  return cn ?? dn
}

// ─── Sortable article block row ───────────────────────────────────────────────

interface SortableArticleBlockProps {
  block: WireArticleBlock
  artifacts: WireArtifact[]
  visibleIdx: number
  visibleBlockCount: number
  isSelected: boolean
  isEditing: boolean
  isFirstVisible: boolean
  isLastBlock: boolean
  blockErrors: ValidationError[]
  hasAnnotations: boolean
  setBlockRef: (el: HTMLElement | null) => void
  onBlockSelect: (id: string | null) => void
  onEnterEdit: (id: string) => void
  onSaveEdit: (blockId: string, children: WireInlineBlock[]) => void
  onCancelEdit: () => void
  onUpdateBlock: (blockId: string, block: WireArticleBlock) => void
  onMoveBlock: (id: string, direction: 'up' | 'down') => void
  onDeleteBlock: (id: string) => void
  onMovePhotoInGroup: (blockId: string, fromIdx: number, toIdx: number) => void
  onSwapComparisonSlots: (blockId: string) => void
  onAutoResolve: (error: ValidationError) => void
  onAcknowledgeError: (error: ValidationError) => void
  blockNotes: WireArticleNote[]
  onAddNote: (blockId: string, content: string, isPrivate: boolean) => void
  onDeleteNote: (noteId: string) => void
  readOnly?: boolean
  notesReadOnly?: boolean
}

function SortableArticleBlock({
  block,
  artifacts,
  visibleIdx,
  visibleBlockCount,
  isSelected,
  isEditing,
  isFirstVisible,
  isLastBlock,
  blockErrors,
  hasAnnotations,
  setBlockRef,
  onBlockSelect,
  onEnterEdit,
  onSaveEdit,
  onCancelEdit,
  onUpdateBlock,
  onMoveBlock,
  onDeleteBlock,
  onMovePhotoInGroup,
  onSwapComparisonSlots,
  onAutoResolve,
  onAcknowledgeError,
  blockNotes,
  onAddNote,
  onDeleteNote,
  readOnly,
  notesReadOnly,
}: SortableArticleBlockProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id })
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const dragHandleProps = { ...attributes, ...listeners }

  const rowContent = (() => {
    // Visual — chrome header + unlinked / linked body
    if (block.type === 'visual') {
      const artifact = artifacts.find(a => a.id === block.artifactId)
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label="Visual"
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          {!block.artifactId || !artifact ? (
            <div className="flex items-center gap-2.5 px-3 py-3">
              <Package size={16} className="shrink-0 text-fg-secondary opacity-40" />
              <p className="text-[11px] italic text-fg-secondary opacity-60">No visual linked — select a .visual artifact in the inspector</p>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-3 py-2">
              <Package size={16} className="shrink-0 text-accent-primary-fg" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-medium text-fg">
                  {block.jobNumber ?? artifact.filename ?? artifact.id}
                </p>
                <p className="text-[10px] text-fg-secondary">
                  {[block.visualType, block.target].filter(Boolean).join(' · ') || 'visual'}
                </p>
              </div>
              {block.visualClassification && (
                <PortionMark classification={block.visualClassification} disseminationControls={block.visualDisseminationControls} />
              )}
            </div>
          )}
        </div>
      )
    }

    // Photo — page-editor chrome with always-visible header (chrome is the drag handle)
    if (block.type === 'photo') {
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label={`Photo · ${block.width ?? 'column'} · ${block.aspectRatio ?? 'natural'}`}
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          {/* Single photo — compact display, not sortable */}
          <div className="flex items-center gap-2.5 px-3 py-2">
            <img
              src={artifacts.find(a => a.id === block.photo.artifactId)?.presignedUrl ?? ''}
              alt=""
              aria-hidden="true"
              className="h-9 w-14 shrink-0 rounded object-cover"
            />
            <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
              {block.photo.caption ?? block.photo.altText ?? (
                <span className="italic text-fg-secondary">Photo</span>
              )}
            </p>
            {block.photo.classification && (
              <PortionMark classification={block.photo.classification} disseminationControls={block.photo.disseminationControls} />
            )}
          </div>
        </div>
      )
    }

    // Video — chrome header with thumbnail preview
    if (block.type === 'video') {
      const videoSrc = artifacts.find(a => a.id === block.artifactId)?.presignedUrl ?? ''
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label={`Video · ${block.width ?? 'column'}`}
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          <div className="flex items-center gap-2.5 px-3 py-2">
            {videoSrc ? (
              <video
                src={videoSrc}
                muted
                playsInline
                preload="metadata"
                className="h-9 w-14 shrink-0 rounded object-cover bg-black"
                aria-hidden="true"
              />
            ) : (
              <div className="flex h-9 w-14 shrink-0 items-center justify-center rounded bg-fill">
                <span className="text-[9px] text-fg-secondary">No video</span>
              </div>
            )}
            <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
              {block.caption ?? (
                <span className="italic text-fg-secondary">
                  {videoSrc ? 'Video' : 'No artifact linked — select one in the inspector'}
                </span>
              )}
            </p>
            <PortionMark classification={block.classification} disseminationControls={block.disseminationControls} />
          </div>
        </div>
      )
    }

    // Audio — chrome header with inline player
    if (block.type === 'audio') {
      const audioSrc = artifacts.find(a => a.id === block.artifactId)?.presignedUrl ?? ''
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label="Audio"
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          <div className="space-y-2 px-3 py-2">
            {audioSrc ? (
              <audio
                src={audioSrc}
                controls
                preload="metadata"
                className="w-full"
                aria-label={block.altText}
              />
            ) : (
              <div className="rounded bg-fill px-2 py-1.5 text-[10px] text-fg-secondary">
                No audio linked — select one in the inspector
              </div>
            )}
            <p className="min-w-0 truncate text-[11px] font-medium text-fg">
              {block.title ?? block.caption ?? (audioSrc ? 'Audio' : 'No artifact linked — select one in the inspector')}
            </p>
            <PortionMark classification={block.classification} disseminationControls={block.disseminationControls} />
          </div>
        </div>
      )
    }

    // Photo comparison — chrome header + sortable slot list
    if (block.type === 'photo-comparison') {
      const beforeLabel = block.beforeLabel ?? 'Before'
      const afterLabel  = block.afterLabel  ?? 'After'
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label={`Photo Comparison · ${beforeLabel} / ${afterLabel}`}
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          <div onClick={e => e.stopPropagation()}>
            <PhotoComparisonSlotList block={block} onSwap={onSwapComparisonSlots} artifacts={artifacts} />
          </div>
        </div>
      )
    }

    // Photo group — outer chrome + inner sortable photo list
    if (block.type === 'photo-group') {
      return (
        <div
          className={`overflow-hidden rounded border-2 transition-colors ${
            isSelected ? 'border-accent-primary-solid' : 'border-line hover:border-line-strong'
          }`}
          onClick={() => onBlockSelect(isSelected ? null : block.id)}
        >
          <BlockChromeHeader
            label={`Photo Group · ${block.photos.length} photo${block.photos.length !== 1 ? 's' : ''} · ${block.width ?? 'column'}`}
            idx={visibleIdx}
            total={visibleBlockCount}
            onMoveUp={() => onMoveBlock(block.id, 'up')}
            onMoveDown={() => onMoveBlock(block.id, 'down')}
            onRemove={() => onDeleteBlock(block.id)}
            dragHandleProps={dragHandleProps}
            readOnly={readOnly}
          />
          {block.groupTitle && (
            <div className="border-b border-line bg-canvas px-3 py-1.5">
              <p className="text-[11px] font-semibold text-fg">{block.groupTitle}</p>
            </div>
          )}
          <div onClick={e => e.stopPropagation()}>
            <PhotoGroupPhotoList block={block} onReorder={onMovePhotoInGroup} artifacts={artifacts} />
          </div>
        </div>
      )
    }

    // Heading in edit mode
    if (block.type === 'heading' && isEditing) {
      return (
        <HeadingBlockEditor
          block={block}
          onSave={(content) => { onUpdateBlock(block.id, { ...block, content }); onCancelEdit() }}
          onCancel={onCancelEdit}
        />
      )
    }

    // Heading: double-click to edit
    if (block.type === 'heading') {
      return (
        <div
          onClick={() => { if (!isEditing) onBlockSelect(block.id) }}
          onDoubleClick={readOnly ? undefined : () => { if (!isEditing) { onEnterEdit(block.id); onBlockSelect(block.id) } }}
          className={`${readOnly ? 'cursor-default' : 'cursor-text'} rounded border-2 p-1.5 transition-colors ${
            isSelected
              ? 'border-accent-primary-solid'
              : blockErrors.length > 0
                ? 'border-error-line hover:border-error-fg'
                : 'border-transparent hover:border-line'
          }`}
        >
          <BlockRenderer block={block} />
        </div>
      )
    }

    // List in edit mode
    if (block.type === 'list' && isEditing) {
      return (
        <ListBlockEditor
          block={block}
          onSave={(items) => { onUpdateBlock(block.id, { ...block, items }); onCancelEdit() }}
          onCancel={onCancelEdit}
        />
      )
    }

    // List: double-click to edit
    if (block.type === 'list') {
      return (
        <div
          onClick={() => { if (!isEditing) onBlockSelect(block.id) }}
          onDoubleClick={readOnly ? undefined : () => { if (!isEditing) { onEnterEdit(block.id); onBlockSelect(block.id) } }}
          className={`${readOnly ? 'cursor-default' : 'cursor-text'} rounded border-2 p-1.5 transition-colors ${
            isSelected
              ? 'border-accent-primary-solid'
              : blockErrors.length > 0
                ? 'border-error-line hover:border-error-fg'
                : 'border-transparent hover:border-line'
          }`}
        >
          <BlockRenderer block={block} />
        </div>
      )
    }

    // Paragraph in edit mode
    if (block.type === 'paragraph' && isEditing) {
      return (
        <ParagraphBlockEditor
          block={block}
          onSave={(children) => onSaveEdit(block.id, children)}
          onCancel={onCancelEdit}
        />
      )
    }

    // Paragraph: single-click selects, double-click enters edit
    if (block.type === 'paragraph') {
      return (
        <div
          onClick={() => { if (!isEditing) onBlockSelect(block.id) }}
          onDoubleClick={readOnly ? undefined : () => { if (!isEditing) { onEnterEdit(block.id); onBlockSelect(block.id) } }}
          className={`${readOnly ? 'cursor-default' : 'cursor-text'} rounded border-2 p-1.5 transition-colors ${
            isSelected
              ? 'border-accent-primary-solid'
              : blockErrors.length > 0
                ? 'border-error-line hover:border-error-fg'
                : 'border-transparent hover:border-line'
          }`}
        >
          <BlockRenderer block={block} />
        </div>
      )
    }

    // All other selectable blocks
    return (
      <div
        onClick={() => onBlockSelect(isSelected ? null : block.id)}
        className={`cursor-pointer rounded border-2 p-1.5 transition-colors ${
          isSelected
            ? 'border-accent-primary-solid'
            : blockErrors.length > 0
              ? 'border-error-line hover:border-error-fg'
              : 'border-transparent hover:border-line'
        }`}
      >
        <BlockRenderer block={block} />
      </div>
    )
  })()

  return (
    <div
      ref={(el) => { setNodeRef(el); setBlockRef(el) }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group/block ${isDragging ? 'opacity-40' : ''} ${hasAnnotations ? 'flex gap-3 items-start' : ''}`}
    >
      {/* Content column — relative so the controls toolbar and grip can anchor here */}
      <div className="relative min-w-0 flex-1">
        {/* Floating grip handle for blocks without a chrome header (visible on row hover) */}
        {!readOnly && !isEditing && block.type !== 'photo' && block.type !== 'photo-group' && block.type !== 'photo-comparison' && (
          <button
            type="button"
            aria-label="Drag to reorder block"
            className="absolute right-1 top-1 z-10 cursor-grab touch-none rounded p-0.5 text-fg-secondary opacity-0 transition-opacity group-hover/block:opacity-50 hover:!opacity-100 active:cursor-grabbing"
            {...dragHandleProps}
          >
            <GripVertical size={12} />
          </button>
        )}
        {isSelected && !isEditing && !readOnly && (
          <div className="absolute left-0 top-0 z-10 -translate-y-full pb-0.5">
            {deleteConfirmId === block.id ? (
              /* Confirm row */
              <div className="flex items-center gap-1 rounded border border-error-line bg-surface px-1.5 py-1 shadow-md">
                <span className="mr-1 text-[10px] text-error-fg">Delete block?</span>
                <button
                  type="button"
                  onClick={() => { onDeleteBlock(block.id); setDeleteConfirmId(null); onBlockSelect(null) }}
                  className="rounded bg-error-fill px-1.5 py-0.5 text-[10px] font-semibold text-error-fg hover:bg-error-line hover:text-white"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  className="rounded px-1.5 py-0.5 text-[10px] text-fg-secondary hover:bg-fill"
                >
                  Cancel
                </button>
              </div>
            ) : (
              /* Normal controls */
              <div className="flex items-center gap-0.5 rounded border border-line bg-surface px-1 py-0.5 shadow-md">
                <button
                  type="button"
                  title="Move up"
                  disabled={isFirstVisible}
                  onClick={() => onMoveBlock(block.id, 'up')}
                  className="rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  title="Move down"
                  disabled={isLastBlock}
                  onClick={() => onMoveBlock(block.id, 'down')}
                  className="rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ChevronDown size={12} />
                </button>
                <div className="mx-0.5 h-3 w-px bg-line" aria-hidden="true" />
                <button
                  type="button"
                  title="Delete block"
                  onClick={() => setDeleteConfirmId(block.id)}
                  className="rounded p-1 text-fg-secondary hover:bg-error-fill hover:text-error-fg"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            )}
          </div>
        )}
        {rowContent}
      </div>
      {hasAnnotations && (
        <div className="w-[210px] shrink-0 space-y-2 pt-1.5">
          {blockErrors.length > 0 && (
            <ValidationAnnotations
              errors={blockErrors}
              onAutoResolve={onAutoResolve}
              onAcknowledge={onAcknowledgeError}
            />
          )}
          <BlockNoteGutter
            blockId={block.id}
            notes={blockNotes}
            onAddNote={onAddNote}
            onDeleteNote={onDeleteNote}
            notesReadOnly={notesReadOnly}
          />
        </div>
      )}
    </div>
  )
}
// ─── Block insert zone (HTML5 drop target between blocks) ────────────────────

function BlockInsertZone({
  afterBlockId,
  onInsert,
}: {
  afterBlockId: string | null
  onInsert: (blockType: string, afterBlockId: string | null) => void
}) {
  const [active, setActive] = useState(false)
  return (
    <div
      className={`relative transition-all duration-100 ${active ? 'h-10' : 'h-3'}`}
      onDragOver={e => {
        if (!e.dataTransfer.types.includes('application/x-wire-block-type')) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
        setActive(true)
      }}
      onDragLeave={() => setActive(false)}
      onDrop={e => {
        e.preventDefault()
        const blockType = e.dataTransfer.getData('application/x-wire-block-type')
        if (blockType) onInsert(blockType, afterBlockId)
        setActive(false)
      }}
    >
      {active && (
        <div className="pointer-events-none absolute inset-x-0 inset-y-1 flex items-center justify-center rounded-md border-2 border-dashed border-accent-primary-solid bg-accent-primary-fill/10">
          <span className="text-[10px] font-semibold text-accent-primary-fg">Drop to insert here</span>
        </div>
      )}
    </div>
  )
}
// ─── ArticleCanvas ────────────────────────────────────────────────────────────

interface Props {
  article: WireArticleFull
  selectedBlockId: string | null
  onBlockSelect: (id: string | null) => void
  blockRefs: MutableRefObject<Record<string, HTMLElement | null>>
  editingBlockId: string | null
  onEnterEdit: (id: string) => void
  onSaveEdit: (blockId: string, children: WireInlineBlock[]) => void
  onCancelEdit: () => void
  onUpdateBlock: (blockId: string, block: WireArticleBlock) => void
  validationErrors: ValidationError[]
  onAutoResolve: (error: ValidationError) => void
  onAcknowledgeError: (error: ValidationError) => void
  onMoveBlock: (id: string, direction: 'up' | 'down') => void
  onDeleteBlock: (id: string) => void
  onMovePhotoInGroup: (blockId: string, fromIdx: number, toIdx: number) => void
  onSwapComparisonSlots: (blockId: string) => void
  onInsertBlock: (blockType: string, afterBlockId: string | null) => void
  onReorderBlocks: (fromBlockId: string, toBlockId: string) => void
  notes?: WireArticleNote[]
  onAddNote?: (blockId: string, content: string, isPrivate: boolean) => void
  onDeleteNote?: (noteId: string) => void
  readOnly?: boolean
  notesReadOnly?: boolean
}

export function ArticleCanvas({ article, selectedBlockId, onBlockSelect, blockRefs, editingBlockId, onEnterEdit, onSaveEdit, onCancelEdit, onUpdateBlock, validationErrors, onAutoResolve, onAcknowledgeError, onMoveBlock, onDeleteBlock, onMovePhotoInGroup, onSwapComparisonSlots, onInsertBlock, onReorderBlocks, notes, onAddNote, onDeleteNote, readOnly, notesReadOnly }: Props) {
  const sources = article.blocks
    .filter((b): b is Extract<WireArticleBlock, { type: 'source-list' }> => b.type === 'source-list')
    .flatMap(b => b.entries)

  // Pre-index errors by blockId for O(1) lookup per block row
  const errorsByBlock = useMemo(() => {
    const map = new Map<string, ValidationError[]>()
    for (const err of validationErrors) {
      if ('blockId' in err) {
        const list = map.get(err.blockId) ?? []
        list.push(err)
        map.set(err.blockId, list)
      }
    }
    return map
  }, [validationErrors])

  const hasBlockAnnotations = errorsByBlock.size > 0

  const notesByBlock = useMemo(() => {
    const map = new Map<string, WireArticleNote[]>()
    for (const note of (notes ?? [])) {
      if (note.blockId) {
        const list = map.get(note.blockId) ?? []
        list.push(note)
        map.set(note.blockId, list)
      }
    }
    return map
  }, [notes])

  const hasRightGutter = !readOnly || !notesReadOnly || hasBlockAnnotations || notesByBlock.size > 0

  const visibleBlocks = useMemo(
    () => article.blocks.filter(
      b => b.type !== 'classification-banner' && b.type !== 'report-details' && b.type !== 'attribution'
    ),
    [article.blocks],
  )

  const dndId = useId()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    onReorderBlocks(String(active.id), String(over.id))
  }

  return (
    <SourceListContext.Provider value={sources}>
    <div className="flex-1 overflow-y-auto">
      {/*
        When there are block annotations, expand max-w to accommodate the 210px
        right gutter while keeping the article text the same visual width.
        Without annotations, just center the content column as before.
      */}
      <div className={`mx-auto px-8 py-8 ${hasRightGutter ? 'max-w-[calc(48rem+230px)]' : 'max-w-3xl'}`}>
        <DndContext id={dndId} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
            <div>
              {!readOnly && <BlockInsertZone afterBlockId={null} onInsert={onInsertBlock} />}
            {visibleBlocks.map((block) => {
              const blockErrors = errorsByBlock.get(block.id) ?? []

              const isSelected = block.id === selectedBlockId
              const isEditing = block.id === editingBlockId
              const visibleIdx = visibleBlocks.findIndex(b => b.id === block.id)

              return (
                <Fragment key={block.id}>
                  <SortableArticleBlock
                    block={block}
                    artifacts={article.artifacts ?? []}
                    visibleIdx={visibleIdx}
                    visibleBlockCount={visibleBlocks.length}
                    isSelected={isSelected}
                    isEditing={isEditing}
                    isFirstVisible={block.id === visibleBlocks[0]?.id}
                    isLastBlock={block.id === visibleBlocks.at(-1)?.id}
                    blockErrors={blockErrors}
                    hasAnnotations={hasRightGutter}
                    setBlockRef={(el) => { blockRefs.current[block.id] = el }}
                    onBlockSelect={onBlockSelect}
                    onEnterEdit={onEnterEdit}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    onUpdateBlock={onUpdateBlock}
                    onMoveBlock={onMoveBlock}
                    onDeleteBlock={onDeleteBlock}
                    onMovePhotoInGroup={onMovePhotoInGroup}
                    onSwapComparisonSlots={onSwapComparisonSlots}
                    onAutoResolve={onAutoResolve}
                    onAcknowledgeError={onAcknowledgeError}
                    blockNotes={notesByBlock.get(block.id) ?? []}
                    onAddNote={onAddNote ?? (() => {})}
                    onDeleteNote={onDeleteNote ?? (() => {})}
                    readOnly={readOnly}
                    notesReadOnly={notesReadOnly}
                  />
                  {!readOnly && <BlockInsertZone afterBlockId={block.id} onInsert={onInsertBlock} />}
                </Fragment>
              )
            })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
    </SourceListContext.Provider>
  )
}
